import React, { FC, useCallback, useState } from 'react';

import { Box, Card, Flex, Heading, Text, Button, Input } from '@opendoor/bricks/core';
import { Modal } from '@opendoor/bricks/complex';
import { showToast } from '@opendoor/bricks/complex/Toast';
import { useDevice } from '@opendoor/bricks/hooks/useMedia';
import { useObservability } from 'shared/helpers/observability';
import { Form } from '@opendoor/cinderblocks/platform/auth/shared';

import { HelloSignTaskFields_signers } from 'sellDirect/helpers/__generated__/HelloSignTaskFields';
import { getHelloSignUrl } from 'sellDirect/helpers/closingDash/helloSign';
import {
  HelloSignReauthRequiredError,
  showToastForHelloSignError,
} from 'sellDirect/helpers/closingDash/errorHandler';
import { useSendInvitation } from 'sellDirect/graphql/useSendInvitation';
import { useReauth } from 'platform/components/ReauthContext';
import { useLazyAsyncFn } from 'shared/helpers/hooks/useLazyAsyncFn';
import { useApolloClient } from '@apollo/client';

export interface InviteeWithEmail extends HelloSignTaskFields_signers {
  email?: string;
  isEmailInvalid?: boolean;
}

export interface HelloSignButtonsProps {
  /** The list of signers available to sign a contract */
  signers: HelloSignTaskFields_signers[];
  /** Callback when user clicks Sign for a document and signatory pair */
  onClickSignButton: (signer: HelloSignTaskFields_signers, helloSignUrl: string) => void;
  /** Whether to display the invite button or not */
  showInvite: boolean;
}

/** Renders buttons for each signer to either sign a contract or send an email invite */
export const HelloSignButtons: FC<HelloSignButtonsProps> = ({
  signers,
  onClickSignButton,
  showInvite,
}) => {
  const observability = useObservability();
  const { showReauth } = useReauth();
  const apolloClient = useApolloClient();

  const [invitee, setInvitee] = useState<undefined | InviteeWithEmail>();
  const [activeSigner, setActiveSigner] = useState<undefined | HelloSignTaskFields_signers>();
  const [isInviteModalOpen, setIsInviteModalOpen] = useState(false);

  const [getUrl, { loading: urlLoading }] = useLazyAsyncFn(getHelloSignUrl);
  const onClickSign = useCallback(
    async (signer: HelloSignTaskFields_signers) => {
      const url = await getUrl(apolloClient, observability, signer);
      if (typeof url === 'string') {
        onClickSignButton(signer, url);
        setActiveSigner(undefined);
      } else if (url === HelloSignReauthRequiredError) {
        showReauth({
          onSuccess: () => onClickSign(signer), // call onClickSign again, which should work the second time around
          onCancel: () => setActiveSigner(undefined),
        });
      } else {
        showToastForHelloSignError(url);
      }
    },
    [apolloClient, observability, getUrl, showReauth, onClickSignButton],
  );

  const { sendInvitation, loading: isSendingInvite } = useSendInvitation({
    onCompleted: () =>
      showToast(
        'info',
        `Invite sent to ${invitee?.email}`,
        'cake-hellosign-send-invitation-info-toast',
        {
          toastOptions: { position: 'bottom-left' },
        },
      ),
    onError: (inviteError) => {
      observability.trackError('send-contract-invite', inviteError);
      showToast(
        'error',
        `Could not send email to ${invitee?.email}`,
        'cake-hellosign-send-invitation-error-toast',
        {
          toastOptions: { position: 'bottom-left' },
        },
      );
      setIsInviteModalOpen(false);
    },
    // If there are any graphql errors, we set the data to undefined and do not run onCompleted
    errorPolicy: 'none',
  });

  /** Handles sending the invitation to another homeowner */
  const onSendInvitation = () => {
    if (invitee?.email && !invitee.isEmailInvalid) {
      sendInvitation(invitee.email, invitee.name, invitee.signatoryId);
      setIsInviteModalOpen(false);
    }
  };

  return (
    <Flex flexDir="column" gridGap="3">
      {signers.map((signer) => (
        <HelloSignButton
          key={signer.signatoryId}
          signer={signer}
          disable={isSendingInvite || urlLoading}
          isLoading={urlLoading && signer.signatoryId === activeSigner?.signatoryId}
          onSignClick={() => {
            setActiveSigner(signer);
            onClickSign(signer);
          }}
          invitation={
            showInvite
              ? {
                  onInvite: () => {
                    setInvitee(signer);
                    setIsInviteModalOpen(true);
                  },
                  isSendingInvite: isSendingInvite && signer.signatoryId === invitee?.signatoryId,
                }
              : undefined
          }
        />
      ))}

      {/* Invite modal */}
      {invitee && showInvite && (
        <InviteModal
          isOpen={isInviteModalOpen}
          invitee={invitee}
          onSendInvitation={onSendInvitation}
          onCloseModal={() => setIsInviteModalOpen(false)}
          setEmail={(email, isEmailInvalid) =>
            setInvitee((currentInvitee) => {
              if (currentInvitee) {
                return {
                  ...currentInvitee,
                  email,
                  isEmailInvalid,
                };
              }
            })
          }
        />
      )}
    </Flex>
  );
};

interface HelloSignButtonProps {
  signer: InviteeWithEmail;
  /** If present, displays invite button. Otherwise does not. */
  invitation?: {
    /** Callback to call for sending an invitation */
    onInvite: () => void;
    /** Whether this specific button is currently sending an invitation */
    isSendingInvite: boolean;
  };
  disable: boolean;
  isLoading: boolean;
  onSignClick: () => void;
}

const HelloSignButton: FC<HelloSignButtonProps> = ({
  signer: { name, signedAt },
  invitation,
  disable,
  isLoading,
  onSignClick,
}) => {
  const { isMobile } = useDevice();
  return (
    <Card variant="secondary" height={['auto', null, null]} p={[7, 7, 7]} pb={[5, 5, 7]}>
      <Flex alignItems="center" flexDir={['column', null, 'row']} gridGap={[3, 3, 5]}>
        <Button
          size="sm"
          aria-label={null}
          analyticsName="cake-sign-contract"
          onClick={onSignClick}
          loading={isLoading}
          disabled={disable || !!signedAt || isLoading}
          width={['100%', null, 250]}
          display="block"
          whiteSpace="nowrap"
          overflow="hidden"
          textOverflow="ellipsis"
          lineHeight="s00"
          textAlign="center"
          pl="4"
        >
          {signedAt
            ? `"${truncateName(name, isMobile ? 22 : 12)}" has signed`
            : `Sign as "${truncateName(name, isMobile ? 25 : 17)}"`}
        </Button>
        {invitation && (
          <Button
            size="sm"
            aria-label="or send email invite for contract"
            analyticsName="cake-send-email-invite-for-contract"
            display="inline-block"
            variant="link"
            width="fit-content"
            height="fit-content"
            onClick={invitation.onInvite}
            loading={invitation.isSendingInvite}
            disabled={invitation.isSendingInvite || !!signedAt || disable || isLoading}
            px="0"
            textAlign="initial"
            lineHeight="s00"
            fontSize={['s0', null, 's1']}
          >
            or send email link
          </Button>
        )}
      </Flex>
    </Card>
  );
};

const truncateName = (name: string, chars = 14): string =>
  name.length < chars ? name : `${name.substring(0, chars).trim()}...`;

interface InviteModalProps {
  invitee: InviteeWithEmail;
  setEmail: (email: string, isEmailValid: boolean) => void;
  onSendInvitation: () => void;
  onCloseModal: () => void;
  isOpen: boolean;
}

const InviteModal: FC<InviteModalProps> = ({
  invitee,
  onSendInvitation,
  setEmail,
  onCloseModal,
  isOpen,
}) => (
  <Modal
    analyticsName="cake-hellosign-send-invitation-modal"
    headerProps={{ title: 'Send email link', titleProps: { as: 'h2' } }}
    isOpen={isOpen}
    onRequestClose={onCloseModal}
  >
    <Box mb="8">
      <Heading size="h4" as="h3">{`Email ${invitee.name}'s contract`}</Heading>
      <Text mt="6">
        If {invitee.name} is not with you, you can email them a link to sign the contract. This will
        give them full access to the dashboard. We’ll let you know once they’ve signed it.
      </Text>
    </Box>

    <Form onSubmit={onSendInvitation}>
      <Input
        analyticsName="cake-hello-sign-button-invitee-email"
        type="email"
        id="invitee-email"
        name="invitee-email"
        value={invitee.email}
        invalid={invitee.isEmailInvalid}
        aria-label={`input email for ${invitee.name}`}
        onChange={(_event, email, validFormat) => {
          setEmail(email || '', !validFormat);
        }}
        label={`${invitee.name}'s email`}
        unboundedWidth
        autoComplete="email"
      />

      <Button
        mt="3"
        aria-label="Send contract invitation by email"
        analyticsName="cake-send-contract-invite-email"
        disabled={!invitee.email || invitee.isEmailInvalid}
        type="submit"
      >
        Send contract
      </Button>
    </Form>
  </Modal>
);
